import { getNamespaceTranslations } from "./questionnaire-translations";

/**
 * Gender purchase gate (RX modal funnel).
 *
 * - **Product** audience: only `custom.gender` (Liquid `data-product-gender` + Storefront
 *   metafield `value`). Tags / handle / title are not used.
 * - **Customer** role: only Shopify customer tags `gender:female` | `gender:male` | `gender:all`.
 *   No `gender:*` tag (or only other tags) → **`all`**.
 * - **Blank / unknown** metafield value → product audience **`all`** (any customer may proceed
 *   for that product until the metafield is set to a specific audience string we recognize).
 * - **Allow** when either side is `all`, or when both are the same `female` / `male`.
 *
 * Enforced when navigating into gated funnel steps (see `GENDER_GATED_FLOW_STEPS` in MDiForm)
 * and on cookie restore / tag–product changes while on those steps.
 */

export type GenderAudience = "female" | "male" | "all";

const GENDER_CUSTOMER_PREFIX = "gender:";

/** Customer gender: only `gender:*` tags; otherwise `all`. */
export function customerTagsToGenderRole(tags: string[]): GenderAudience {
  const lowered = tags.map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (lowered.includes(`${GENDER_CUSTOMER_PREFIX}all`)) return "all";
  if (lowered.includes(`${GENDER_CUSTOMER_PREFIX}female`)) return "female";
  if (lowered.includes(`${GENDER_CUSTOMER_PREFIX}male`)) return "male";
  return "all";
}

/** Normalize Storefront / Liquid metafield text (JSON string, quotes, whitespace). */
function normalizeGenderMetafieldValue(metafieldValue: string | null | undefined): string {
  let raw = (metafieldValue ?? "").trim();
  if (!raw) return "";
  if (raw.startsWith('"') && raw.endsWith('"')) {
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === "string") raw = parsed;
      else raw = raw.slice(1, -1);
    } catch {
      raw = raw.slice(1, -1);
    }
  }
  return raw.trim().toLowerCase();
}

function genderAudienceFromCustomGenderMetafield(
  metafieldValue: string | null | undefined
): GenderAudience {
  const raw = normalizeGenderMetafieldValue(metafieldValue);
  if (!raw || raw === "0") return "all";
  if (raw === "female" || raw === "femenino" || raw === "for her" || raw === "for-her") return "female";
  if (raw === "male" || raw === "masculino" || raw === "for him" || raw === "for-him") return "male";
  if (raw === "all" || raw === "unisex" || raw === "both") return "all";

  return "all";
}

export function canCustomerPurchaseProduct(
  customerRole: GenderAudience,
  productAudience: GenderAudience
): boolean {
  if (customerRole === "all") return true;
  if (productAudience === "all") return true;
  return customerRole === productAudience;
}

export function resolveCustomerTagsForGenderRole(
  data: { accountData?: { shopifyCustomerTags?: string[] } | null } | undefined,
  flowAccountData: { shopifyCustomerTags?: string[] } | null | undefined,
  customerTagsCsv: string | undefined
): string[] {
  const buckets: string[][] = [];
  const push = (tags: string[] | undefined | null) => {
    if (Array.isArray(tags) && tags.length > 0) buckets.push(tags);
  };
  push(data?.accountData?.shopifyCustomerTags);
  push(flowAccountData?.shopifyCustomerTags);
  const fromCsv = customerTagsCsv
    ? customerTagsCsv.split(",").map((s) => s.trim()).filter(Boolean)
    : [];
  if (fromCsv.length) buckets.push(fromCsv);

  const seen = new Set<string>();
  const out: string[] = [];
  for (const bucket of buckets) {
    for (const t of bucket) {
      const key = t.trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push(t.trim());
    }
  }
  return out;
}

export type ProductGenderSources = {
  pageGenderMetafield?: string;
  graphGenderMetafield?: string | null;
};

type StorefrontGenderMetafield = {
  key?: string | null;
  value?: string | null;
} | null;

/** Read Storefront `custom.gender` string (`value`) for this product. */
export function extractGenderMetafieldFromStorefrontProduct(product: {
  metafields?: StorefrontGenderMetafield[] | null;
} | null): string | null {
  const mf = product?.metafields?.find((m) => m?.key === "gender");
  const raw = mf?.value?.trim();
  return raw ? raw : null;
}

export function resolveProductGenderAudience(sources: ProductGenderSources): GenderAudience {
  const mf = sources.graphGenderMetafield ?? sources.pageGenderMetafield;
  return genderAudienceFromCustomGenderMetafield(mf);
}

const GENDER_PURCHASE_MISMATCH_FALLBACK =
  "This product is not available for your account profile. Please contact support or choose the product that matches your profile.";

export function alertGenderPurchaseMismatch(language: "en" | "es"): void {
  const accountT = getNamespaceTranslations(language, "account");
  window.alert(accountT?.genderPurchaseMismatchError ?? GENDER_PURCHASE_MISMATCH_FALLBACK);
}
